import type {
  CfbVisionCandidateStatus,
  CfbVisionConfidenceTier,
  CfbVisionCoverageTier,
  CfbVisionIntakeCandidate,
} from './cfbScheduleVisionTypes';

export const CFB_VISION_INTAKE_MIN_IMAGES = 1;
export const CFB_VISION_INTAKE_MAX_IMAGES = 12;
export const CFB_VISION_INTAKE_ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const CFB_VISION_INTAKE_MAX_IMAGE_BYTES = 8 * 1024 * 1024;

/** Validate a selected screenshot batch before upload. Returns an error string or null. */
export function validateIntakeFiles(files: File[]): string | null {
  if (!files || files.length < CFB_VISION_INTAKE_MIN_IMAGES) {
    return 'Add at least one screenshot.';
  }
  if (files.length > CFB_VISION_INTAKE_MAX_IMAGES) {
    return `Upload at most ${CFB_VISION_INTAKE_MAX_IMAGES} screenshots per batch.`;
  }
  for (const file of files) {
    if (!CFB_VISION_INTAKE_ALLOWED_TYPES.includes(file.type)) {
      return 'Only PNG, JPEG, or WebP screenshots are supported.';
    }
    if (file.size > CFB_VISION_INTAKE_MAX_IMAGE_BYTES) {
      return 'Each screenshot must be 8 MB or smaller.';
    }
  }
  return null;
}

export function confidenceTierLabel(tier: CfbVisionConfidenceTier | string | undefined): string {
  switch (tier) {
    case 'high':
      return 'High confidence';
    case 'medium':
      return 'Medium confidence';
    case 'low':
      return 'Low confidence';
    case 'unusable':
      return 'Unusable';
    default:
      return 'Unknown';
  }
}

export function coverageTierLabel(tier: CfbVisionCoverageTier | string | undefined): string {
  switch (tier) {
    case 'high':
      return 'Coverage looks complete';
    case 'medium':
      return 'Coverage partially verified';
    case 'low':
      return 'Coverage needs review';
    default:
      return 'Coverage not verified';
  }
}

export function candidateStatusLabel(
  status: CfbVisionCandidateStatus | string | undefined
): string {
  switch (status) {
    case 'ready':
      return 'Ready';
    case 'imported':
      return 'Imported';
    case 'rejected':
      return 'Ignored';
    case 'needs_review':
    default:
      return 'Needs review';
  }
}

const WARNING_LABELS: Record<string, string> = {
  home_school_not_assigned: 'Home school is not assigned in CFB Setup.',
  away_school_not_assigned: 'Away school is not assigned in CFB Setup.',
  home_school_unmatched: 'Home school could not be matched.',
  away_school_unmatched: 'Away school could not be matched.',
  ambiguous_school_text: 'A school name was ambiguous.',
  missing_week: 'No week number was detected.',
  home_away_unclear: 'Home vs away was unclear.',
  home_and_away_identical: 'Home and away are the same team.',
  duplicate_candidate: 'Duplicate of another extracted row.',
  already_exists_in_schedule: 'A matching game already exists in the schedule.',
};

export function describeCandidateWarning(code: string): string {
  return WARNING_LABELS[code] ?? code;
}

export interface CandidateWeekGroup {
  weekNumber: number | null;
  weekLabel: string;
  candidates: CfbVisionIntakeCandidate[];
}

/** Group candidates by week (nulls last), then order within a week by source image + row. */
export function groupCandidatesByWeek(
  candidates: CfbVisionIntakeCandidate[]
): CandidateWeekGroup[] {
  const buckets = new Map<string, CfbVisionIntakeCandidate[]>();
  for (const candidate of candidates) {
    const key = candidate.week_number == null ? 'none' : String(candidate.week_number);
    const list = buckets.get(key) ?? [];
    list.push(candidate);
    buckets.set(key, list);
  }

  const groups: CandidateWeekGroup[] = [];
  Array.from(buckets.entries()).forEach(([key, list]) => {
    const sorted = list.slice().sort((a, b) => {
      const imageA = a.source_image_index ?? 0;
      const imageB = b.source_image_index ?? 0;
      if (imageA !== imageB) return imageA - imageB;
      return (a.row_index ?? 0) - (b.row_index ?? 0);
    });
    const weekNumber = key === 'none' ? null : Number(key);
    groups.push({
      weekNumber,
      weekLabel: weekNumber == null ? 'Week unknown' : `Week ${weekNumber}`,
      candidates: sorted,
    });
  });

  groups.sort((a, b) => {
    if (a.weekNumber == null) return 1;
    if (b.weekNumber == null) return -1;
    return a.weekNumber - b.weekNumber;
  });
  return groups;
}

/** Candidate ids that are eligible to import (matched both schools, has week, not rejected/imported/unusable). */
export function importableCandidateIds(candidates: CfbVisionIntakeCandidate[]): string[] {
  return candidates
    .filter(
      (candidate) =>
        candidate.candidate_status !== 'imported' &&
        candidate.candidate_status !== 'rejected' &&
        candidate.confidence_tier !== 'unusable' &&
        candidate.week_number != null &&
        Boolean(candidate.home_dynasty_assignment_id) &&
        Boolean(candidate.away_dynasty_assignment_id)
    )
    .map((candidate) => candidate.id);
}
